import { useMemo, useRef } from 'react';
import { Panel } from '../ui';
import { normalizeString, findCloseMatch } from '../utils';
import './ArticleDisplay.css';

const PUNCTUATION = /^[.,;:!?()[\]{}""''—–-]$/;
const SPLIT_PATTERN = /(\s+|[.,;:!?()[\]{}""''—–-])/;
const STRIP_PATTERN = /[.,;:!?()[\]{}""''—–-]/g;

/* ============================================================================
   ArticleDisplay — the redacted document.

   Words the player hasn't found are rendered as blank tiles keeping the
   original character count, which is the actual information the game gives
   you. Words that were revealed by the guess that just landed get a one-shot
   pop so the eye is pulled to what changed — without that, finding a common
   word in a wall of text produces no visible reaction at all.
   ========================================================================== */

function ArticleDisplay({ article, guessedWords, gameWon = false }) {
  /* Which words appeared with the guess that just landed.
     Keyed on the identity of the incoming Set (Game replaces it on every
     guess) rather than recomputed per render, so React.StrictMode's double
     render in dev can't consume the diff on the first pass and leave the
     second pass with nothing to highlight. */
  const diff = useRef({ source: null, previous: new Set(), fresh: new Set() });

  if (diff.current.source !== guessedWords) {
    const fresh = new Set();
    for (const word of guessedWords) {
      if (!diff.current.previous.has(word)) fresh.add(word);
    }

    // A guess adds exactly one entry to the set (one normalized word, however
    // many times it occurs in the article). Anything larger is a bulk change —
    // saved progress being restored on load — and highlighting all of it would
    // strobe the entire document instead of pointing at what just changed.
    diff.current = {
      source: guessedWords,
      previous: new Set(guessedWords),
      fresh: fresh.size > 1 ? new Set() : fresh,
    };
  }

  const freshWords = diff.current.fresh;

  const renderText = (text) => {
    if (!text) return null;

    return text.split(SPLIT_PATTERN).map((token, index) => {
      const key = `${index}-${token}`;

      if (/^\s+$/.test(token) || PUNCTUATION.test(token)) {
        return <span key={key} className="doc__punct">{token}</span>;
      }

      const normalized = normalizeString(token.replace(STRIP_PATTERN, ''));
      const isGuessed = guessedWords.has(normalized);

      if (gameWon || isGuessed) {
        const isFresh = !gameWon && freshWords.has(normalized);
        return (
          <span key={key} className={`doc__word ${isFresh ? 'is-fresh' : ''}`}>
            {token}
          </span>
        );
      }

      // Not found, but close to a guess already made (stem or typo match) —
      // show that guess in place of the blank, styled as unconfirmed, so the
      // player knows they're on the right track without giving away the word.
      const closeGuess = findCloseMatch(normalized, guessedWords);
      if (closeGuess) {
        const isFresh = freshWords.has(closeGuess);
        return (
          <span
            key={key}
            className={`doc__close ${isFresh ? 'is-fresh' : ''}`}
            title="Close, but not the exact word"
          >
            {closeGuess}
          </span>
        );
      }

      // Hide every Unicode letter and digit, so this works for any language's
      // Wikipedia — not just Latin scripts.
      return (
        <span key={key} className="doc__blank" aria-label="hidden word">
          {token.replace(/[\p{L}\p{N}]/gu, '_')}
        </span>
      );
    });
  };

  const renderedTitle = useMemo(
    () => renderText(article?.title),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [article, guessedWords, gameWon]
  );

  const renderedContent = useMemo(
    () => renderText(article?.content),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [article, guessedWords, gameWon]
  );

  if (!article) return null;

  return (
    <div className="doc">
      <Panel
        variant={gameWon ? 'hot' : 'default'}
        label="Target"
        corner={gameWon ? 'DECRYPTED' : 'CLASSIFIED'}
        className={`doc__target ${gameWon ? 'is-solved' : ''}`}
        glow={gameWon}
      >
        <h2 className="doc__title">{renderedTitle}</h2>
      </Panel>

      <Panel variant="screen" className="doc__screen">
        <div className="doc__body">{renderedContent}</div>
      </Panel>
    </div>
  );
}

export default ArticleDisplay;
